import React, { useEffect, useState } from 'react';
import axios from 'axios';

const initialForm = {
  name: '',
  url: '',
  email: '',
  legacy_token: '',
};

const AdministratorInstances = () => {
  const [instances, setInstances] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [error, setError] = useState('');

  const fetchInstances = () => {
    setLoading(true);
    setError('');

    axios
      .get('/api/administrator/instances')
      .then((response) => {
        setInstances(response.data || []);
      })
      .catch(() => {
        setError('No fue posible cargar las instancias.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchInstances();
  }, []);

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const payload = {
      ...form,
      email: form.email || null,
      legacy_token: form.legacy_token || null,
    };

    const request = editingId
      ? axios.put(`/api/administrator/instances/${editingId}`, payload)
      : axios.post('/api/administrator/instances', payload);

    request
      .then(() => {
        resetForm();
        fetchInstances();
      })
      .catch((err) => {
        setError(err?.response?.data?.message || 'No fue posible guardar la instancia.');
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const handleEdit = (instance) => {
    setEditingId(instance.id);
    setForm({
      name: instance.name || '',
      url: instance.url || '',
      email: instance.email || '',
      legacy_token: instance.legacy_token || '',
    });
  };

  const handleGenerateToken = (instance) => {
    setGeneratingId(instance.id);
    setError('');

    axios
      .post(`/api/administrator/instances/${instance.id}/generate-token`)
      .then((response) => {
        const updatedInstance = response.data;

        setInstances((prev) =>
          prev.map((item) => (item.id === updatedInstance.id ? updatedInstance : item))
        );

        if (editingId === updatedInstance.id) {
          setForm((prev) => ({
            ...prev,
            email: updatedInstance.email || '',
            legacy_token: updatedInstance.legacy_token || '',
          }));
        }
      })
      .catch((err) => {
        setError(err?.response?.data?.message || 'No fue posible generar el token.');
      })
      .finally(() => {
        setGeneratingId(null);
      });
  };

  const handleDelete = (instance) => {
    if (!window.confirm(`¿Eliminar la instancia ${instance.name}?`)) {
      return;
    }

    setError('');

    axios
      .delete(`/api/administrator/instances/${instance.id}`)
      .then(() => {
        if (editingId === instance.id) {
          resetForm();
        }
        fetchInstances();
      })
      .catch(() => {
        setError('No fue posible eliminar la instancia.');
      });
  };

  return (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-5">
        <h3 className="mb-0">Instancias</h3>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="row">
        <div className="col-md-4">
          <div className="card card-custom gutter-b">
            <div className="card-body">
              <h5 className="mb-4">{editingId ? 'Editar Instancia' : 'Alta de Instancia'}</h5>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="font-weight-bolder">Nombre</label>
                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="font-weight-bolder">URL</label>
                  <input
                    type="url"
                    name="url"
                    className="form-control"
                    value={form.url}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="font-weight-bolder">Email</label>
                  <input
                    type="email"
                    name="email"
                    className="form-control"
                    value={form.email}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="font-weight-bolder">Token legacy</label>
                  <textarea
                    name="legacy_token"
                    className="form-control"
                    rows="4"
                    value={form.legacy_token}
                    onChange={handleChange}
                  />
                </div>

                <div className="d-flex flex-wrap">
                  <button
                    type="submit"
                    className="btn btn-primary font-weight-bolder mr-3 mb-2"
                    disabled={saving}
                  >
                    {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
                  </button>

                  {editingId && (
                    <>
                      <button
                        type="button"
                        className="btn btn-light-success font-weight-bolder mr-3 mb-2"
                        onClick={() => handleGenerateToken({ id: editingId, name: form.name })}
                        disabled={Boolean(generatingId) || saving}
                      >
                        {generatingId === editingId ? 'Generando...' : 'Generar token'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-light-primary font-weight-bolder mb-2"
                        onClick={resetForm}
                        disabled={saving}
                      >
                        Cancelar
                      </button>
                    </>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-md-8">
          <div className="card card-custom">
            <div className="card-body">
              <h5 className="mb-4">Listado de Instancias</h5>

              {loading ? (
                <p className="text-muted mb-0">Cargando...</p>
              ) : instances.length === 0 ? (
                <p className="text-muted mb-0">No hay instancias registradas.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-head-custom table-vertical-center">
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>URL</th>
                        <th>Email</th>
                        <th>Token legacy</th>
                        <th className="text-right">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {instances.map((instance) => (
                        <tr key={instance.id}>
                          <td>{instance.name}</td>
                          <td>{instance.url}</td>
                          <td>{instance.email || '-'}</td>
                          <td>{instance.legacy_token ? 'Configurado' : '-'}</td>
                          <td className="text-right">
                            <button
                              type="button"
                              className="btn btn-sm btn-light-success font-weight-bolder mr-2"
                              onClick={() => handleGenerateToken(instance)}
                              disabled={generatingId === instance.id}
                            >
                              {generatingId === instance.id ? 'Generando...' : 'Generar token'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-light-primary font-weight-bolder mr-2"
                              onClick={() => handleEdit(instance)}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-light-danger font-weight-bolder"
                              onClick={() => handleDelete(instance)}
                            >
                              Eliminar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdministratorInstances;
